import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Computer as ComputerIcon,
  Code as CodeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../../api';
import { showSuccess, showError } from '../../../utils/sweetAlertConfig';
import PageHeader from '../../../components/PageHeader';
import AssignCandidatesModal from '../../../components/AssignCandidatesModal';
import CreateExamModal from '../../../components/CreateExamModal';
import CreateTechnicalTaskModal from '../../../components/CreateTechnicalTaskModal';
import AssessmentDetailsModal from '../../../components/AssessmentDetailsModal';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";

export default function AssessmentManagement() {
  const [mainTab, setMainTab] = useState(0); // 0: Exams, 1: Tasks
  const [exams, setExams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Modal states
  const [showExamModal, setShowExamModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchExams(), fetchTasks(), fetchJobs()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      setExams(response.data || []);
    } catch (error) {
      console.error('Failed to fetch exams');
      setExams([]);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get('/technical-tasks');
      const tasksData = response.data?.technical_assessments || response.data || [];
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to fetch tasks');
      setTasks([]);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      const jobsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setJobs(jobsData);
    } catch (error) {
      console.error('Failed to fetch jobs');
      setJobs([]);
    }
  };

  const handleAssignToJob = (item) => {
    setSelectedItem(item);
    setShowAssignModal(true);
  };

  const filteredExams = exams.filter(exam =>
    exam.title?.toLowerCase().includes(search.toLowerCase()) ||
    exam.exam_id?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredTasks = tasks.filter(task =>
    task.title?.toLowerCase().includes(search.toLowerCase()) ||
    task.task_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Assessment Management"
        subtitle="Create and manage digital assessments and technical tasks"
      />

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowExamModal(true)}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#1a1635' } }}
        >
          Create Digital Exam
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowTaskModal(true)}
          sx={{ bgcolor: SUCCESS, '&:hover': { bgcolor: '#4a8455' } }}
        >
          Create Technical Task
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search by title or ID..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}>
          <Tab icon={<ComputerIcon />} label="Digital Exams" iconPosition="start" />
          <Tab icon={<CodeIcon />} label="Technical Tasks" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Digital Exams Table */}
      {mainTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Exam ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Job</strong></TableCell>
                <TableCell><strong>Questions</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No digital exams found. Create one to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredExams.map((exam) => (
                  <TableRow key={exam.exam_id} hover>
                    <TableCell>{exam.exam_id}</TableCell>
                    <TableCell>{exam.title}</TableCell>
                    <TableCell>{exam.job_title || 'N/A'}</TableCell>
                    <TableCell>{exam.questions_count || 0}</TableCell>
                    <TableCell>{exam.duration} min</TableCell>
                    <TableCell>
                      <Chip
                        label={exam.status || 'draft'}
                        size="small"
                        color={exam.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Assign to Job">
                        <IconButton
                          size="small"
                          onClick={() => handleAssignToJob(exam)}
                        >
                          <ComputerIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Technical Tasks Table */}
      {mainTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Task ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Job</strong></TableCell>
                <TableCell><strong>Deadline (Days)</strong></TableCell>
                <TableCell><strong>Max Score</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No technical tasks found. Create one to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow key={task.task_id} hover>
                    <TableCell>{task.task_id}</TableCell>
                    <TableCell>{task.title}</TableCell>
                    <TableCell>{task.job_title || 'N/A'}</TableCell>
                    <TableCell>{task.deadline_days}</TableCell>
                    <TableCell>{task.max_score || 100}</TableCell>
                    <TableCell>
                      <Chip
                        label={task.status || 'draft'}
                        size="small"
                        color={task.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Assign to Job">
                        <IconButton
                          size="small"
                          onClick={() => handleAssignToJob(task)}
                        >
                          <CodeIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modals */}
      <CreateExamModal
        open={showExamModal}
        onClose={() => setShowExamModal(false)}
        onSuccess={() => {
          setShowExamModal(false);
          fetchExams();
        }}
        jobs={jobs}
      />

      <CreateTechnicalTaskModal
        open={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onSuccess={() => {
          setShowTaskModal(false);
          fetchTasks();
        }}
        jobs={jobs}
      />

      <AssignCandidatesModal
        open={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedItem(null);
        }}
        assessment={selectedItem}
        jobs={jobs}
        onSuccess={loadData}
      />
    </Container>
  );
}
